import type { IntentDispatchParams } from '@kehto/services';
import { originRegistry } from '@kehto/shell';

import {
  BrowserIntentController,
  type BrowserIntentGeneration,
} from './browser-intent-controller.js';
import {
  activateRuntimeTab,
  addRuntimeTab,
  closeRuntimeTab,
  renderRuntimeTabs,
  runtimeTabGenerationId,
  type PajaRuntimeTab,
} from './browser-runtime-tabs.js';
import { appendPajaMessageLog } from './browser-devtools.js';
import { getPajaRelayUrls } from './browser-relay-runtime.js';
import { isPajaLocalTarget } from './local-target.js';
import {
  matchesInstalledNappletRecord,
  type InstalledNappletCatalog,
} from './installed-napplet-catalog.js';
import {
  resolvePajaPointer,
  type PajaResolvedPointer,
} from './runtime-resolver.js';
import type {
  PajaBrowserState,
  PajaBrowserStateContext,
  PajaHostRuntimeState,
} from './browser-host.js';

export interface PajaIntentHostEffects {
  persistTabs?(state: PajaBrowserState): void;
  setReadyStatus?(state: PajaBrowserState): void;
  onDelivered?(params: IntentDispatchParams): void;
  onTerminal?(params: IntentDispatchParams, reason: import('./browser-intent-controller.js').BrowserIntentTerminalReason): void;
  /** Observe retained work before asynchronous target completion. */
  onAccepted?(params: IntentDispatchParams, acceptance: import('@kehto/services').IntentTargetAcceptance): void;
}

/**
 * Create the browser target operations used by canonical intent dispatch.
 *
 * @param getState - Returns the live Paja browser state.
 * @param getContext - Returns the live Paja host context.
 * @param effects - Host-owned persistence and lifecycle effects.
 * @returns Intent target operations bound to the current Paja host.
 */
export function createPajaIntentTargetOptions(
  getState: () => PajaBrowserState | null,
  getContext: () => PajaBrowserStateContext | null,
  effects: PajaIntentHostEffects = {},
): ConstructorParameters<typeof BrowserIntentController>[0] {
  return {
    onDelivered: effects.onDelivered,
    onTerminal: effects.onTerminal,
    onAccepted: effects.onAccepted,
    async openOrReuse(params) {
      const state = getState();
      const context = getContext();
      if (!state || !context) return null;
      const record = context.runtime.catalog.get(params.handler);
      if (!record || !recordSupportsDelivery(record, params)) return null;

      // A catalog replacement may retain the d-tag while replacing the verified
      // aggregate. Remove stale tabs before choosing any live delivery target.
      // Local development tabs never came from the catalog: they are neither
      // stale catalog tabs nor eligible verified delivery targets.
      for (const stale of state.tabs) {
        if (
          !isPajaLocalTarget(stale.resolvedTarget)
          && catalogIdForTarget(stale.resolvedTarget) === params.handler
          && !matchesInstalledNappletRecord(record, catalogIdentity(stale.resolvedTarget))
        ) closeRuntimeTab(state, context, stale.id);
      }

      const current = state.tabs.find((tab) =>
        !isPajaLocalTarget(tab.resolvedTarget)
        && matchesInstalledNappletRecord(record, catalogIdentity(tab.resolvedTarget))
        && isCurrentRuntimeTabGeneration(state, context, tab),
      );
      if (
        current
        && params.behavior?.reuse !== false
      ) {
        // A delivered intent is a navigation, so the handler surface must become
        // the visible one, matching the new-tab path below. Paja is a single-stage
        // tab workspace: a tab is either selected or invisible, so an unselected
        // handler would silently receive a payload the user cannot see. NAP-INTENT
        // defines `behavior` fields as hints that "runtime workspace and lifecycle
        // policy remain authoritative"
        // (`napplet/naps`, NAP-INTENT.md, draft `nap-intent` a718915d; "Focus the
        // target surface" on master a040914b), so `behavior.focus` — `false`
        // included — does not demote delivery to a hidden tab. Reuse still leaves
        // the caller's tab open, so nothing is replaced or hidden from the tab bar.
        activateRuntimeTab(state, context, current.id);
        effects.persistTabs?.(state);
        return bindPajaIntentGeneration(current, record, context.runtime);
      }

      const resolved = await resolvePajaPointer(record.pointer.value, pajaPointerResolverOptions(context));
      if (
        resolved.manifest.catalogId !== record.id
        || resolved.aggregateHash !== record.aggregateHash
        || !resolvedSupportsDelivery(resolved, params)
      ) return null;

      const currentRecord = context.runtime.catalog.validateCurrent(record, catalogIdentity(resolved));
      if (!currentRecord) return null;
      const tab = addRuntimeTab(state, context, currentRecord.pointer.value, resolved);
      effects.persistTabs?.(state);
      return bindPajaIntentGeneration(tab, currentRecord, context.runtime);
    },
    waitForReady(generation) {
      const state = getState();
      const context = getContext();
      if (!state || !context) return Promise.reject(new Error('Paja host is not available'));
      const tab = findRuntimeTabGeneration(state, generation);
      if (!tab || !isCurrentPajaIntentGeneration(generation, state, context, tab)) {
        invalidatePajaIntentGeneration(generation, context.runtime);
        return Promise.reject(new Error('intent target generation is not current'));
      }
      if (tab.windowId && context.runtime.readyWindowIds.has(tab.windowId)) return undefined;
      return new Promise<void>((resolve, reject) => {
        const waiters = context.runtime.readyWaiters.get(generation) ?? new Set();
        waiters.add({ resolve, reject });
        context.runtime.readyWaiters.set(generation, waiters);
      });
    },
    isCurrent(generation) {
      const state = getState();
      const context = getContext();
      const tab = state && context ? findRuntimeTabGeneration(state, generation) : null;
      if (!tab || !state || !context || !isCurrentPajaIntentGeneration(generation, state, context, tab)) {
        if (context) invalidatePajaIntentGeneration(generation, context.runtime);
        return false;
      }
      return true;
    },
    send(generation, params) {
      const state = getState();
      const context = getContext();
      const tab = state && context ? findRuntimeTabGeneration(state, generation) : null;
      if (!state || !context || !tab || !tab.windowId || !isCurrentPajaIntentGeneration(generation, state, context, tab)) {
        if (context) invalidatePajaIntentGeneration(generation, context.runtime);
        throw new Error('intent target generation is not current and ready');
      }
      const source = tab.frame.contentWindow;
      if (!source || originRegistry.getWindowId(source) !== tab.windowId) {
        throw new Error('intent target source is no longer registered');
      }
      // NIP-5D: delivered over the tab's bound napplet.hello port.
      originRegistry.postToNapplet(tab.windowId, {
        type: 'intent.deliver',
        delivery: {
          sender: params.sender,
          archetype: params.archetype,
          action: params.action,
          convention: params.convention,
          ...(params.payload === undefined ? {} : { payload: params.payload }),
        },
      });
    },
  };
}

/**
 * Record one verified napplet's declared intent surface in the Paja message log.
 *
 * Catalog eligibility is filtered separately by the target intent domain.
 * INC declarations are not required for canonical intent delivery.
 *
 * @param state - Current Paja browser state.
 * @param resolvedTarget - Resolver-verified pointer that was just installed.
 */
export function recordInstalledIntentSurface(
  state: PajaBrowserState,
  resolvedTarget: PajaResolvedPointer,
): void {
  const archetypes = resolvedTarget.manifest.archetypes;
  const requires = resolvedTarget.manifest.requires;
  const intentEligible = Boolean(resolvedTarget.dTag) && archetypes.length > 0;
  appendPajaMessageLog(state, 'paja', {
    type: 'paja.pointer.resolved',
    dTag: resolvedTarget.dTag,
    aggregateHash: resolvedTarget.aggregateHash,
    archetypes,
    requires,
    intentEligible,
  });
}

/**
 * Build resolver options from the host pointer config and simulated relay set.
 *
 * @param context - Current Paja browser context.
 * @returns Resolver options for a verified runtime pointer.
 */
export function pajaPointerResolverOptions(context: PajaBrowserStateContext) {
  return {
    relays: [
      ...(context.config.target.pointer?.relays ?? []),
      ...getPajaRelayUrls(context.runtime.currentSimulation),
    ],
    blossomServers: context.config.target.pointer?.blossomServers ?? [],
    maxWaitMs: context.config.target.pointer?.maxWaitMs,
  };
}

function recordSupportsDelivery(
  record: ReturnType<InstalledNappletCatalog['installed']>[number],
  params: IntentDispatchParams,
): boolean {
  return record.archetypes.some((archetype) =>
    archetype.slug === params.archetype
    && archetype.convention === params.convention,
  );
}

function resolvedSupportsDelivery(
  resolved: PajaResolvedPointer,
  params: IntentDispatchParams,
): boolean {
  return resolved.manifest.archetypes.some((archetype) =>
    archetype.slug === params.archetype
    && archetype.convention === params.convention,
  );
}

function findRuntimeTabGeneration(
  state: PajaBrowserState,
  generation: BrowserIntentGeneration,
): PajaRuntimeTab | null {
  return state.tabs.find((tab) => runtimeTabGenerationId(tab) === generation.id) ?? null;
}

function bindPajaIntentGeneration(
  tab: PajaRuntimeTab,
  record: NonNullable<ReturnType<InstalledNappletCatalog['get']>>,
  runtime: PajaHostRuntimeState,
): BrowserIntentGeneration {
  const generation = { id: runtimeTabGenerationId(tab) };
  runtime.intentRecords.set(generation, record);
  return generation;
}

function isCurrentPajaIntentGeneration(
  generation: BrowserIntentGeneration,
  state: PajaBrowserState,
  context: PajaBrowserStateContext,
  tab: PajaRuntimeTab,
): boolean {
  const record = context.runtime.intentRecords.get(generation);
  return record !== undefined
    && !isPajaLocalTarget(tab.resolvedTarget)
    && context.runtime.catalog.validateCurrent(record, catalogIdentity(tab.resolvedTarget)) !== null
    && isCurrentRuntimeTabGeneration(state, context, tab);
}

function invalidatePajaIntentGeneration(
  generation: BrowserIntentGeneration,
  runtime: PajaHostRuntimeState,
): void {
  const waiters = runtime.readyWaiters.get(generation);
  if (!waiters) return;
  runtime.readyWaiters.delete(generation);
  for (const waiter of waiters) waiter.reject(new Error('intent target catalog record was replaced'));
}

/**
 * Reject retained readiness waits when their selected catalog record changes.
 *
 * @param state - Current Paja browser state.
 * @param context - Current Paja browser context.
 * @returns A function that removes the catalog subscription.
 */
export function subscribePajaIntentCatalogChanges(
  state: PajaBrowserState,
  context: PajaBrowserStateContext,
): () => void {
  return context.runtime.catalog.onChanged((id) => {
    for (const [generation] of context.runtime.readyWaiters) {
      const record = context.runtime.intentRecords.get(generation);
      if (!record || record.id !== id) continue;
      const tab = findRuntimeTabGeneration(state, generation);
      if (!tab || !isCurrentPajaIntentGeneration(generation, state, context, tab)) {
        invalidatePajaIntentGeneration(generation, context.runtime);
      }
    }
  });
}

function catalogIdForTarget(target: PajaRuntimeTab['resolvedTarget']): string | undefined {
  return isPajaLocalTarget(target) ? undefined : target.manifest.catalogId;
}

function catalogIdentity(target: PajaResolvedPointer): Pick<ReturnType<InstalledNappletCatalog['installed']>[number], 'id' | 'aggregateHash'> {
  return { id: target.manifest.catalogId, aggregateHash: target.aggregateHash };
}

function isCurrentRuntimeTabGeneration(
  state: PajaBrowserState,
  context: PajaBrowserStateContext,
  tab: PajaRuntimeTab,
): boolean {
  const source = tab.frame.contentWindow;
  return state.tabs.includes(tab)
    && tab.windowId !== null
    && source !== null
    && originRegistry.getWindowId(source) === tab.windowId
    && runtimeTabGenerationId(tab) === `${tab.id}:${tab.generation}`;
}

/**
 * Resolve ready waiters for a verified live runtime tab generation.
 *
 * @param state - Current Paja browser state.
 * @param context - Current Paja browser context.
 * @param tab - Runtime tab reporting readiness.
 * @param source - Message source that reported readiness.
 * @param registeredWindowId - Window ID currently registered for the source.
 * @param effects - Host-owned lifecycle effects.
 * @returns Whether the ready message belonged to the current tab generation.
 */
export function markRuntimeTabReady(
  state: PajaBrowserState,
  context: PajaBrowserStateContext,
  tab: PajaRuntimeTab,
  source: Window,
  registeredWindowId: string | null,
  effects: PajaIntentHostEffects = {},
): boolean {
  if (
    !tab.windowId
    || tab.frame.contentWindow !== source
    || registeredWindowId !== tab.windowId
    || !isCurrentRuntimeTabGeneration(state, context, tab)
  ) return false;
  context.runtime.readyWindowIds.add(tab.windowId);
  for (const [generation, waiters] of context.runtime.readyWaiters) {
    if (findRuntimeTabGeneration(state, generation) !== tab) continue;
    context.runtime.readyWaiters.delete(generation);
    if (!isCurrentPajaIntentGeneration(generation, state, context, tab)) {
      for (const waiter of waiters) waiter.reject(new Error('intent target catalog record was replaced'));
      continue;
    }
    for (const waiter of waiters) waiter.resolve();
  }
  tab.status = 'ready';
  if (state.activeTabId === tab.id) {
    if (effects.setReadyStatus) effects.setReadyStatus(state);
    else state.status = 'ready';
  }
  renderRuntimeTabs(state);
  return true;
}

/**
 * Reject retained readiness waits for a runtime tab generation being replaced.
 *
 * @param tab - Runtime tab whose generation is being cleared.
 * @param runtime - Paja runtime state that owns the retained waiters.
 */
export function clearRuntimeTabGeneration(
  tab: PajaRuntimeTab,
  runtime: PajaHostRuntimeState,
): void {
  for (const [generation, waiters] of runtime.readyWaiters) {
    if (generation.id !== runtimeTabGenerationId(tab)) continue;
    runtime.readyWaiters.delete(generation);
    for (const waiter of waiters) waiter.reject(new Error('intent target generation replaced'));
  }
}
